// Shared semantic colors/labels for eval verdicts — every place that shows a
// resolution, satisfaction or severity must read the same at a glance.

export const RES_PT: Record<string, string> = {
  resolved: 'Resolvida', partially_resolved: 'Parcial', not_resolved: 'Não resolvida',
  abandoned: 'Abandonada', no_task: 'Sem pedido',
}
export const SAT_PT: Record<string, string> = {
  satisfied: 'Satisfeito', neutral: 'Neutro', frustrated: 'Frustrado', angry: 'Irritado', unknown: 'Sem sinal',
}

export const RES_ORDER = ['resolved', 'partially_resolved', 'not_resolved', 'abandoned', 'no_task']
export const SAT_ORDER = ['satisfied', 'neutral', 'frustrated', 'angry', 'unknown']

// Histogram bar fills
export const RES_BAR: Record<string, string> = {
  resolved: 'bg-emerald-500',
  partially_resolved: 'bg-yellow-400',
  not_resolved: 'bg-red-500',
  abandoned: 'bg-red-700',
  no_task: 'bg-zinc-300 dark:bg-zinc-600',
}
export const SAT_BAR: Record<string, string> = {
  satisfied: 'bg-emerald-500',
  neutral: 'bg-zinc-400',
  frustrated: 'bg-orange-500',
  angry: 'bg-red-600',
  unknown: 'bg-zinc-300 dark:bg-zinc-600',
}

// Small status chips (ledger rows, session panel)
const CHIP_GREEN = 'border-emerald-300 bg-emerald-50 text-emerald-800 dark:border-emerald-500/40 dark:bg-emerald-950/40 dark:text-emerald-300'
const CHIP_YELLOW = 'border-yellow-300 bg-yellow-50 text-yellow-800 dark:border-yellow-500/40 dark:bg-yellow-950/40 dark:text-yellow-300'
const CHIP_ORANGE = 'border-orange-300 bg-orange-50 text-orange-800 dark:border-orange-500/40 dark:bg-orange-950/40 dark:text-orange-300'
const CHIP_RED = 'border-red-300 bg-red-50 text-red-800 dark:border-red-500/40 dark:bg-red-950/40 dark:text-red-300'
const CHIP_GRAY = 'border-border bg-muted text-muted-foreground'

export const RES_CHIP: Record<string, string> = {
  resolved: CHIP_GREEN,
  partially_resolved: CHIP_YELLOW,
  not_resolved: CHIP_RED,
  abandoned: CHIP_RED,
  no_task: CHIP_GRAY,
}
export const SAT_CHIP: Record<string, string> = {
  satisfied: CHIP_GREEN,
  neutral: CHIP_GRAY,
  frustrated: CHIP_ORANGE,
  angry: CHIP_RED,
}

// Severity is its OWN axis (worst quality problem of the session) — independent
// of resolution. Ramp green -> amber -> orange -> red: "nenhuma" is green (no
// quality problem = good) and it intensifies toward red as the problem worsens.
export const SEV_DOT: Record<string, string> = {
  none: 'bg-emerald-500', minor: 'bg-amber-400', major: 'bg-orange-500', critical: 'bg-red-600',
}

// Severity labels (PT) for tooltips/legend.
export const SEV_PT: Record<string, string> = {
  none: 'nenhuma', minor: 'leve', major: 'grave', critical: 'crítico',
}

// The dot's green ("nenhuma") must mean "sem problema", so it depends on BOTH
// severity and the problem flag. Any real severity (minor/major/critical) is
// kept as-is — a resolved session can legitimately carry a minor quality blip
// and should still show leve, not green. Only a "none"-severity row branches:
// leve (amber) when it's still a problem (e.g. uma não-resolvida), else green.
export function severityKey(severity: string, problem: boolean): string {
  if (severity !== 'none') return severity
  return problem ? 'minor' : 'none'
}

// Lens accents (left border of each section in the verdict details)
export const LENS_ACCENT: Record<string, string> = {
  outcome: 'border-violet-400',
  execution: 'border-cyan-500',
  adherence: 'border-amber-500',
  safety: 'border-red-500',
  meta: 'border-zinc-300 dark:border-zinc-600',
}

// Evidence source chip per lens family ("exec:http_4xx" -> exec)
export function evidenceSrcClass(src: string): string {
  if (src.startsWith('exec')) return 'bg-cyan-100 text-cyan-800 dark:bg-cyan-500/15 dark:text-cyan-300'
  if (src.startsWith('adherence')) return 'bg-amber-100 text-amber-800 dark:bg-amber-500/15 dark:text-amber-300'
  if (src.startsWith('safety')) return 'bg-red-100 text-red-800 dark:bg-red-500/15 dark:text-red-300'
  return 'bg-violet-100 text-violet-800 dark:bg-violet-500/15 dark:text-violet-300' // outcome
}
